// Zusatzstellen und Zusatzzugänge für Start, Growth und Scale. Die Stepper
// schreiben direkt career_count und seat_count. Zusatzstellen rechnen den
// Talentspeicher mit +50 je Stelle nach (storageFor), solange CS ihn nicht von
// Hand geändert hat. Der Listenpreis ist nur ein Hinweis.
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid'

import {
  PLANS,
  STORAGE_PER_ADDON_CAREER
} from '../../constants/pricing-catalog'
import { productTitle } from '../../constants/products'
import { classNames } from '../../helper'
import {
  formatCount,
  isUnlimited,
  listPrice,
  storageFor
} from '../../helper/pricing'
import { formatEuro } from './PlanPresetSelector'

const FINITE_PLANS = ['start', 'grow', 'scale']

export const hasAddons = (planId) => FINITE_PLANS.includes(planId)

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

const clamp = (n, min, max) => Math.min(Math.max(n, min), max)

/**
 * Ob der Talentspeicher noch der Formel folgt. Leer zählt als „folgt", dann
 * gibt es einen Vorschlag.
 */
export const isStorageDerived = (values, planId) => {
  if (!hasAddons(planId)) return false
  const storage = toNumber(values?.spaces_count)
  if (storage === null) return true

  const careers = toNumber(values?.career_count)
  return storage === storageFor(planId, careers === null ? undefined : careers)
}

/**
 * Zusatzmengen aus den Formularwerten. maxExtra null heißt beliebig.
 */
export const addonState = (values, planId) => {
  if (!hasAddons(planId)) return null
  const plan = PLANS[planId]

  const resource = (key, field) => {
    const included = plan.limits[key]
    const max = plan.max[key]
    const value = toNumber(values?.[field])
    const unlimited = value !== null && isUnlimited(value)

    return {
      included,
      max: Number.isFinite(max) ? max : null,
      maxExtra: Number.isFinite(max) ? max - included : null,
      extra: value === null || unlimited ? 0 : Math.max(0, value - included),
      unlimited,
      overMax:
        !unlimited && Number.isFinite(max) && value !== null && value > max
    }
  }

  return {
    careers: resource('careers', 'career_count'),
    seats: resource('seats', 'seat_count')
  }
}

/**
 * Setzt Zusatzstellen oder Zusatzzugänge. Werte außerhalb von 0 bis zum
 * Paketmaximum werden begrenzt. Der Talentspeicher folgt den Zusatzstellen nur,
 * wenn er vorher der Formel entsprach.
 *
 * @param {object} values Formularwerte
 * @param {string} planId start, grow oder scale
 * @param {{ careers?: number, seats?: number }} extra Zusatzmengen
 */
export const applyAddonChange = (values, planId, { careers, seats } = {}) => {
  const state = addonState(values, planId)
  if (!state) return values

  const next = { ...values }

  if (typeof careers === 'number' && !Number.isNaN(careers)) {
    const extra = clamp(Math.floor(careers), 0, state.careers.maxExtra)
    const careerCount = state.careers.included + extra
    if (isStorageDerived(values, planId)) {
      next.spaces_count = storageFor(planId, careerCount)
    }
    next.career_count = careerCount
  }

  if (typeof seats === 'number' && !Number.isNaN(seats)) {
    const max = state.seats.maxExtra === null ? Infinity : state.seats.maxExtra
    next.seat_count = state.seats.included + clamp(Math.floor(seats), 0, max)
  }

  return next
}

/**
 * Setzt career_count aus dem Feld in den Basisdaten. Dieselbe Regel wie der
 * Stepper. Folgte der Talentspeicher vorher der Formel, rechnet er mit, sonst
 * bleibt er stehen. Ohne Zusatzoptionen (Unlimited, Individuell, kein Paket)
 * und bei ungültigem oder unbegrenztem Wert ändert sich nur career_count. Ein
 * leeres Feld setzt einen der Formel folgenden Speicher auf den Paketwert.
 *
 * @param {object} values Formularwerte vor der Änderung
 * @param {string|null} planId
 * @param {number|string} careerCount neuer Wert, '' für leer
 */
export const applyCareerCountChange = (values, planId, careerCount) => {
  const next = { ...values, career_count: careerCount }
  const careers = toNumber(careerCount)
  // Beim Tippen ist das Feld kurz leer. Folgte der Speicher der Formel, geht
  // er auf den Paketwert, so passt er weiter zur Formel und rechnet mit dem
  // nächsten Wert wieder mit. Sonst gälte er ab hier als von Hand gesetzt.
  if (careers === null && isStorageDerived(values, planId)) {
    next.spaces_count = storageFor(planId)
    return next
  }
  if (
    !hasAddons(planId) ||
    careers === null ||
    careers < 1 ||
    !Number.isInteger(careers) ||
    isUnlimited(careers) ||
    !isStorageDerived(values, planId)
  ) {
    return next
  }
  next.spaces_count = storageFor(planId, careers)
  return next
}

const Stepper = ({ id, label, value, max, disabled, onChange, hint }) => {
  const atMin = value <= 0
  const atMax = max !== null && value >= max
  const buttonClass =
    'inline-flex h-9 w-9 items-center justify-center text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'

  return (
    <div>
      <label
        htmlFor={id}
        className='block text-sm font-medium leading-6 text-gray-900'
      >
        {label}
      </label>
      <div className='mt-2 flex items-center'>
        <button
          type='button'
          aria-label={`${label} verringern`}
          className={classNames(buttonClass, 'rounded-l-md')}
          disabled={disabled || atMin}
          onClick={() => onChange(value - 1)}
        >
          <MinusIcon className='h-4 w-4' aria-hidden='true' />
        </button>
        <input
          id={id}
          type='number'
          min={0}
          max={max === null ? undefined : max}
          value={value}
          disabled={disabled}
          onChange={(event) => {
            const n = parseInt(event.target.value, 10)
            onChange(Number.isNaN(n) ? 0 : n)
          }}
          className='h-9 w-20 border-0 p-0 text-center text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:bg-gray-50'
        />
        <button
          type='button'
          aria-label={`${label} erhöhen`}
          className={classNames(buttonClass, 'rounded-r-md')}
          disabled={disabled || atMax}
          onClick={() => onChange(value + 1)}
        >
          <PlusIcon className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
      {hint && <p className='mt-1 text-xs text-gray-500'>{hint}</p>}
    </div>
  )
}

/**
 * @param {object} props
 * @param {string|null} props.planId gewähltes Paket, nur Start, Growth und
 *   Scale haben Zusatzoptionen
 * @param {object} props.values Formularwerte
 * @param {(nextValues: object) => void} props.onChange
 * @param {boolean} [props.disabled]
 */
const AddonFields = ({ planId, values, onChange, disabled = false }) => {
  const state = addonState(values, planId)
  if (!state) return null

  const plan = PLANS[planId]
  const price = listPrice(planId, {
    careerCount: values?.career_count,
    seatCount: values?.seat_count,
    products: values?.products
  })
  const derived = isStorageDerived(values, planId)
  const expectedStorage = storageFor(
    planId,
    toNumber(values?.career_count) ?? undefined
  )

  const careerHint = state.careers.unlimited
    ? 'Stellen sind unbegrenzt gesetzt.'
    : `+${formatEuro(plan.addonPrice.career)} je Stelle, +${STORAGE_PER_ADDON_CAREER} Talentspeicher je Stelle, bis ${formatCount(state.careers.maxExtra)} Zusatzstellen`
  const seatHint = state.seats.unlimited
    ? 'Zugänge sind unbegrenzt gesetzt.'
    : `+${formatEuro(plan.addonPrice.seat)} je Zugang, ${
        state.seats.maxExtra === null
          ? 'beliebig zubuchbar'
          : `bis ${formatCount(state.seats.maxExtra)} Zusatzzugänge`
      }`

  return (
    <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
      <h3 className='text-sm font-semibold text-gray-900'>Zusatzoptionen</h3>
      <div className='mt-3 grid grid-cols-1 gap-6 sm:grid-cols-2'>
        <Stepper
          id='addon_careers'
          label='Zusatzstellen'
          value={state.careers.extra}
          max={state.careers.maxExtra}
          disabled={disabled || state.careers.unlimited}
          hint={careerHint}
          onChange={(careers) =>
            onChange(applyAddonChange(values, planId, { careers }))
          }
        />
        <Stepper
          id='addon_seats'
          label='Zusatzzugänge'
          value={state.seats.extra}
          max={state.seats.maxExtra}
          disabled={disabled || state.seats.unlimited}
          hint={seatHint}
          onChange={(seats) =>
            onChange(applyAddonChange(values, planId, { seats }))
          }
        />
      </div>

      {!derived && expectedStorage !== null && (
        <div className='mt-3 flex flex-wrap items-center gap-x-3 text-xs text-gray-600'>
          <span>
            Talentspeicher ist von Hand gesetzt, Zusatzstellen ändern ihn nicht.
            Nach Formel wären es {formatCount(expectedStorage)}.
          </span>
          <button
            type='button'
            disabled={disabled}
            className='font-semibold text-indigo-600 hover:text-indigo-500 disabled:opacity-40'
            onClick={() =>
              onChange({ ...values, spaces_count: expectedStorage })
            }
          >
            Neu berechnen
          </button>
        </div>
      )}

      {price && (
        <div className='mt-4 border-t border-gray-200 pt-3 text-sm text-gray-700'>
          <div className='flex flex-wrap items-center gap-x-3'>
            <span>
              Listenpreis bei 12 Monaten{' '}
              <span className='font-semibold tabular-nums'>
                {formatEuro(price.monthly)} / Monat
              </span>
            </span>
            <button
              type='button'
              disabled={disabled}
              className='text-sm font-semibold text-indigo-600 hover:text-indigo-500 disabled:opacity-40'
              onClick={() => onChange({ ...values, price: price.monthly })}
            >
              Als Preis übernehmen
            </button>
          </div>
          {price.breakdown.length > 1 && (
            <p className='mt-1 text-xs text-gray-500'>
              {price.breakdown
                .map(({ label, amount }) => `${label} ${formatEuro(amount)}`)
                .join(', ')}
            </p>
          )}
          {typeof price.flexMonthly === 'number' && (
            <p className='mt-1 text-xs text-gray-500'>
              Monatlich kündbar {formatEuro(price.flexMonthly)} / Monat
            </p>
          )}
          {price.onRequest.length > 0 && (
            <p className='mt-1 text-xs text-gray-500'>
              Preis auf Anfrage für{' '}
              {price.onRequest
                .map((product) => productTitle(product))
                .join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default AddonFields
